import { useCallback, useMemo, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { EquipmentForm } from "@/components/equipment/EquipmentForm";
import { EquipmentTagPicker } from "@/components/equipment/EquipmentTagPicker";
import {
  useEquipment,
  useCreateEquipment,
  type Equipment,
  type EquipmentInsert,
} from "@/hooks/useEquipment";
import { useEquipmentInstances } from "@/hooks/useEquipmentInstances";
import { cn } from "@/lib/utils";

function EquipmentLinkCell({
  children,
  className,
  to,
}: {
  children: ReactNode;
  className?: string;
  to: string;
}) {
  return (
    <TableCell className={cn("p-0", className)}>
      <Link to={to} className="block h-full p-4 text-inherit no-underline">
        {children}
      </Link>
    </TableCell>
  );
}

function getPageNumbers(
  current: number,
  total: number,
): (number | "ellipsis")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "ellipsis")[] = [1];
  if (current > 3) pages.push("ellipsis");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push("ellipsis");
  pages.push(total);
  return pages;
}

const EquipmentPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Derive filter state from URL params
  const search = searchParams.get("q") || "";
  const selectedTagIds = useMemo(() => {
    const raw = searchParams.get("tags");
    return raw ? raw.split(",").filter(Boolean) : [];
  }, [searchParams]);
  const page = Number(searchParams.get("page") || "1");

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data, isLoading, isFetching } = useEquipment({
    search,
    tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    page,
  });
  const equipment = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;

  const createEquipment = useCreateEquipment();

  const handleCreate = (data: EquipmentInsert, tagIds: string[]) => {
    createEquipment.mutate(
      { equipment: data, tagIds },
      {
        onSuccess: (newEquipment) => {
          setIsCreateDialogOpen(false);
          navigate(`/equipment/${newEquipment.id}`);
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Equipment">
        <Button
          offlineEdit
          onClick={() => setIsCreateDialogOpen(true)}
          data-testid="new-equipment-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Equipment
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={(v) => {
              updateParams({ q: v || null, page: null });
            }}
            placeholder="Search by name, manufacturer, number, short name..."
          />
        </div>
        <div className="w-full sm:w-64">
          <EquipmentTagPicker
            selectedTagIds={selectedTagIds}
            showEquipmentCount
            onChange={(ids) => {
              updateParams({
                tags: ids.length > 0 ? ids.join(",") : null,
                page: null,
              });
            }}
            placeholder="Filter by tags..."
            displayMode="compact"
          />
        </div>
      </div>

      <div
        className={`border rounded-lg overflow-hidden transition-opacity ${isFetching && !isLoading ? "opacity-60" : ""}`}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Manufacturer</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Stock/Instances</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Loading equipment...
                </TableCell>
              </TableRow>
            ) : equipment.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Package className="h-8 w-8" />
                    <p>No equipment found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              equipment.map((item) => (
                <EquipmentRow key={item.id} equipment={item} />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {totalCount} item{totalCount !== 1 ? "s" : ""} total
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    updateParams({
                      page: page > 2 ? String(page - 1) : null,
                    })
                  }
                  aria-disabled={page === 1}
                  className={
                    page === 1
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
              {getPageNumbers(page, totalPages).map((p, i) =>
                p === "ellipsis" ? (
                  <PaginationItem key={`e-${i}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={p}>
                    <PaginationLink
                      isActive={p === page}
                      onClick={() =>
                        updateParams({
                          page: p > 1 ? String(p) : null,
                        })
                      }
                      className="cursor-pointer"
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    updateParams({
                      page: page < totalPages ? String(page + 1) : String(page),
                    })
                  }
                  aria-disabled={page === totalPages}
                  className={
                    page === totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Equipment</DialogTitle>
          </DialogHeader>
          <EquipmentForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
            isLoading={createEquipment.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EquipmentRow = ({ equipment }: { equipment: Equipment }) => {
  const { data: instances = [] } = useEquipmentInstances(
    equipment.is_serialized ? equipment.id : undefined,
  );

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      data-testid={`equipment-row-${equipment.id}`}
    >
      <EquipmentLinkCell
        to={`/equipment/${equipment.id}`}
        className="font-mono text-sm"
      >
        {equipment.number}
      </EquipmentLinkCell>
      <EquipmentLinkCell
        to={`/equipment/${equipment.id}`}
        className="font-medium"
      >
        <span data-testid={`equipment-row-name-${equipment.id}`}>
          {equipment.name}
        </span>
      </EquipmentLinkCell>
      <EquipmentLinkCell to={`/equipment/${equipment.id}`}>
        {equipment.manufacturer || "-"}
      </EquipmentLinkCell>
      <EquipmentLinkCell to={`/equipment/${equipment.id}`}>
        <div className="flex flex-wrap gap-1">
          {equipment.tags?.slice(0, 3).map((tag) => (
            <Badge key={tag.id} variant="outline" className="text-xs">
              {tag.name}
            </Badge>
          ))}
          {equipment.tags && equipment.tags.length > 3 && (
            <Badge variant="outline" className="text-xs">
              +{equipment.tags.length - 3}
            </Badge>
          )}
        </div>
      </EquipmentLinkCell>
      <EquipmentLinkCell to={`/equipment/${equipment.id}`}>
        {equipment.is_serialized
          ? `${instances.length} instances`
          : `${equipment.stock_quantity} in stock`}
      </EquipmentLinkCell>
    </TableRow>
  );
};

export default EquipmentPage;
